import React, { useState, useEffect, useMemo } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';

// Importando o Provedor e o Hook do contexto de autenticação
import { AuthProvider, useAuth } from './context/AuthContext';

// --- DEPENDÊNCIAS DO MATERIAL-UI ---
import {
    Box, AppBar as MuiAppBar, Toolbar, IconButton, Drawer as MuiDrawer, List, ListItem,
    ListItemButton, ListItemIcon, ListItemText, Divider, Menu, MenuItem, CssBaseline,
    ThemeProvider, createTheme, styled, alpha, Tooltip, Avatar,
    Paper, Typography, Button, CircularProgress, Container, Grid
} from '@mui/material';
import { keyframes } from '@mui/system';

// --- ÍCONES DO MATERIAL-UI ---
import {
    Menu as MenuIcon, Logout, Person, Home, AccountTree, ArrowBack as ArrowBackIcon, CorporateFare,
    BarChart as BarChartIcon, Assessment as AssessmentIcon, ArrowForward as ArrowForwardIcon, QueryStats,
    Dashboard as DashboardIcon, EmojiEvents as TrophyIcon, HelpOutline as HelpIcon
} from '@mui/icons-material';

// --- IMPORT DA LOGO ---
import logoImage from './assets/logonav.png';

// --- IMPORT COMPONENTES ---
import ManualsModal from './components/ManualsModal';

// --- IMPORT DAS PÁGINAS ---
import OrganogramPageWrapper from './pages/OrganogramPage';
import CorporateCompetenciesPage from './pages/CorporateCompetenciesPage';
import ChartsPage from './pages/ChartsPage';
import EmployeeAnalysisPage from './pages/EmployeeAnalysisPage'; 
import AdminOverviewPage from './pages/AdminOverviewPage';
import TopPerformersPage from './pages/TopPerformersPage';

// ============================================================================
// COMPONENTE DE LAYOUT (DASHBOARDLAYOUT)
// ============================================================================
const DashboardLayout = ({ children }) => {
    const [open, setOpen] = useState(false);
    const [anchorElProfile, setAnchorElProfile] = useState(null);
    const [manualsOpen, setManualsOpen] = useState(false);
    
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // ✨ VERIFICAÇÃO SE ESTÁ EMBUTIDO NO EAD (MEMBERKIT)
    const isEmbedded = window.self !== window.top;

    // ✨ LÓGICA DE GESTOR (isManager) - INTEGRADA E ROBUSTA
    const isManager = useMemo(() => {
        // ... (mantenha a lógica intacta)
    }, [user]);

    const isAdminOrEditor = user && ['superadmin', 'admin', 'editor'].includes(user.role);
    const canViewManagement = isAdminOrEditor || isManager;

    const menuItems = [
        { text: 'Início', icon: <Home />, path: '/' },
        { text: 'Organograma', icon: <AccountTree />, path: '/organograma' },
        ...(canViewManagement ? [
            { text: 'Visão Geral de Avaliações', icon: <DashboardIcon />, path: '/visao-geral' },
            { text: 'Destaques', icon: <TrophyIcon />, path: '/destaques' }
        ] : []),
        { text: 'Competências', icon: <CorporateFare />, path: '/competencias-corporativas' },
    ];

    const handleDrawerToggle = () => setOpen(!open);
    const handleDrawerClose = () => setOpen(false);
    const handleProfileMenuOpen = (event) => setAnchorElProfile(event.currentTarget);
    const handleProfileMenuClose = () => setAnchorElProfile(null);
    const handleLogout = () => {
        handleProfileMenuClose();
        logout();
    };

    const theme = createTheme({
        palette: { primary: { main: '#ff834f' } },
        typography: { fontFamily: "'Inter', sans-serif" }
    });

    const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
        padding: '12px 24px',
        color: theme.palette.text.secondary,
        '&:hover': { backgroundColor: alpha(theme.palette.common.black, 0.04) },
        '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            color: theme.palette.primary.main,
            fontWeight: 'bold',
            '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.12) }
        },
    }));

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ display: 'flex' }}>
                <CssBaseline />
                
                {/* 👇 SE NÃO ESTIVER EMBUTIDO, MOSTRA A NAVBAR SUPERIOR 👇 */}
                {!isEmbedded && (
                    <MuiAppBar position="fixed" elevation={0} sx={{ zIndex: 1099, backgroundColor: '#050a30', borderBottom: `1px solid ${alpha('#FFFFFF', 0.1)}` }}>
                        <Toolbar>
                            <IconButton onClick={handleDrawerToggle} edge="start" sx={{ color: '#FFFFFF', marginRight: 2 }}><MenuIcon /></IconButton>
                            <div onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                <Box component="img" sx={{ height: 40, display: { xs: 'none', sm: 'block' } }} alt="Logo" src={logoImage} />
                            </div>
                            <Box sx={{ flexGrow: 1 }} />

                            <Tooltip title="Opções de Perfil">
                                <IconButton onClick={handleProfileMenuOpen} size="small">
                                    <Avatar sx={{ width: 32, height: 32 }} src={user?.avatarUrl ? `${process.env.REACT_APP_UPLOADS_URL}/${user.avatarUrl.replace(/\\/g, '/')}` : ''}>
                                        {user?.name ? user.name[0].toUpperCase() : 'U'}
                                    </Avatar>
                                </IconButton>
                            </Tooltip>
                            <Menu anchorEl={anchorElProfile} open={Boolean(anchorElProfile)} onClose={handleProfileMenuClose}>
                                <MenuItem onClick={() => { handleProfileMenuClose(); window.location.href = `${process.env.REACT_APP_COCKPIT_URL}/perfil`; }}><ListItemIcon><Person fontSize="small" /></ListItemIcon>Meu Perfil</MenuItem>
                                <Divider />
                                <MenuItem onClick={handleLogout}><ListItemIcon><Logout fontSize="small" /></ListItemIcon>Sair</MenuItem>
                            </Menu>
                        </Toolbar>
                    </MuiAppBar>
                )}

                {/* 👇 A SIDEBAR SÓ PODE ABRIR SE NÃO ESTIVER EMBUTIDO 👇 */}
                {!isEmbedded && (
                    <MuiDrawer anchor="left" open={open} onClose={handleDrawerClose} variant="temporary" sx={{ '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 260, marginTop: '64px', height: 'calc(100vh - 64px)' } }}>
                        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                            <List>
                                {menuItems.map((item) => (
                                    <ListItem key={item.text} disablePadding>
                                        <StyledListItemButton onClick={() => { navigate(item.path); handleDrawerClose(); }} selected={location.pathname === item.path}>
                                            <ListItemIcon sx={{ color: location.pathname === item.path ? 'primary.main' : 'inherit' }}>{item.icon}</ListItemIcon>
                                            <ListItemText primary={item.text} />
                                        </StyledListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                        <Divider />
                        <List>
                            <ListItem disablePadding>
                                <StyledListItemButton onClick={() => window.location.href = process.env.REACT_APP_COCKPIT_URL}>
                                    <ListItemIcon><ArrowBackIcon /></ListItemIcon>
                                    <ListItemText primary="Voltar à Plataforma" />
                                </StyledListItemButton>
                            </ListItem>
                        </List>
                    </MuiDrawer>
                )}

                {/* 👇 O CONTEÚDO PRINCIPAL 👇 */}
                <Box component="main" sx={{ 
    flexGrow: 1, 
    p: isEmbedded ? 0 : { xs: 2, sm: 3 }, /* 👈 Se for iframe o padding é zero */
    width: '100%', 
    minHeight: '100vh', 
    bgcolor: isEmbedded ? '#ffffff' : '#f0f2f5' /* Fica branco no EAD e cinza no painel */
}}>
                    
                    {/* 👇 O espaçador da Navbar só aparece se a Navbar existir 👇 */}
                    {!isEmbedded && <Toolbar />}
                    
                    {children}
                </Box>
            </Box>
        </ThemeProvider>
    );
};

// ============================================================================
// COMPONENTE DE PÁGINA INICIAL (HOMEPAGE)
// ============================================================================
const fadeInUp = keyframes`from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); }`;

const FeatureCard = styled(Paper)(({ theme, delay = 0, glowColor }) => ({
    position: 'relative', 
    overflow: 'hidden', 
    padding: theme.spacing(3), 
    height: '100%', 
    borderRadius: '20px', 
    backgroundColor: '#FFFFFF',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.07)', 
    transition: 'transform 0.3s ease, box-shadow 0.4s ease', 
    animation: `${fadeInUp} 0.6s ease-out forwards`,
    animationDelay: `${delay}s`, 
    opacity: 0, 
    border: '1px solid #E5E7EB',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    '&:before': { 
        content: '""', 
        position: 'absolute', 
        top: 0, 
        left: 0, 
        width: '100%', 
        height: '100%', 
        background: `radial-gradient(circle at 50% 0, ${alpha(glowColor, 0.15)}, transparent 40%)`, 
        opacity: 0, 
        transition: 'opacity 0.4s ease' 
    },
    '&:hover': { 
        transform: 'translateY(-12px)', 
        boxShadow: `0 20px 40px ${alpha(glowColor, 0.2)}`, 
        '&:before': { opacity: 1 } 
    },
}));

const IconWrapper = styled(Box)(({ theme, color }) => ({
    width: 56, 
    height: 56, 
    borderRadius: '16px', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center', 
    marginBottom: theme.spacing(2),
    color: '#FFFFFF', 
    background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`, 
    boxShadow: `0 6px 12px ${alpha(color, 0.3)}`, 
    fontSize: '28px',
}));

const HomePage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [isManualsOpen, setIsManualsOpen] = useState(true); 

    // Lógica robusta de GESTOR (isManager)
    const isManager = useMemo(() => {
        if (!user) return false;
        const u = user;
        const termo = 'vaga gerencial';
        const checkText = (f) => f && f.toString().toLowerCase().includes(termo);
        return u.isGerencial || u.is_gerencial === true || checkText(user.jobDescription) || checkText(user.cargo) || (user.cargo && /gerente|lider|líder|coordena|supervisor/i.test(user.cargo));
    }, [user]);

    const isAdminOrEditor = user && ['superadmin', 'admin', 'editor'].includes(user.role);
    const canViewManagement = isAdminOrEditor || isManager;

    const modules = [
        { icon: <AccountTree />, color: '#22C55E', title: 'Organograma', description: 'Explore a estrutura hierárquica e funcional.', path: '/organograma' },
        { icon: <CorporateFare />, color: '#3B82F6', title: 'Competências', description: 'Mapeamento de cargos e dicionário de competências.', path: '/competencias-corporativas' },
        ...(canViewManagement ? [
            { icon: <DashboardIcon />, color: '#F59E0B', title: 'Visão Geral de Avaliações', description: 'Progresso das avaliações do seu time em tempo real.', path: '/visao-geral' },
            { icon: <TrophyIcon />, color: '#EAB308', title: 'Destaques da Minha Equipe', description: 'Ranking de performance para reconhecimento.', path: '/destaques' }
        ] : []),
        //{ icon: <QueryStats />, color: '#8B5CF6', title: 'Gráficos', description: 'Dashboards com tendências e insights.', path: '/graficos' }
    ];

    return (
        <Box sx={{ minHeight: 'calc(100vh - 150px)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <ManualsModal isOpen={isManualsOpen} onClose={() => setIsManualsOpen(false)} isAutoOpen={true} />
            
            <Container maxWidth="xl">
                <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#111827', mb: 1, fontSize: { xs: '2rem', md: '2.5rem', lg: '3rem' } }}>
                        Software de Gestão por Competências
                    </Typography>
                    
                    <Typography variant="h6" sx={{ color: '#6B7280', fontWeight: 400, maxWidth: '900px', mx: 'auto' }}>
                        Bem-vindo(a), <Box component="span" sx={{ color: 'primary.main', fontWeight: '700' }}>{user?.name || 'Usuário'}</Box>! Otimize o potencial humano da sua empresa.
                    </Typography>
                </Box>

                <Grid container spacing={2.5} justifyContent="center" alignItems="stretch">
                    {modules.map((mod, index) => (
                        <Grid item xs={12} sm={6} md={4} lg={canViewManagement ? 2 : 3} key={index}>
                            <FeatureCard elevation={0} delay={index * 0.1} glowColor={mod.color} onClick={() => navigate(mod.path)} sx={{ cursor: 'pointer' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                                    <IconWrapper color={mod.color}>{mod.icon}</IconWrapper>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1F2937', mb: 1, lineHeight: 1.2 }}>
                                        {mod.title}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#6B7280', display: { xs: 'none', lg: 'block' }, mb: 2, height: '40px', overflow: 'hidden' }}>
                                        {mod.description}
                                    </Typography>
                                    <Button variant="text" size="small" endIcon={<ArrowForwardIcon />} sx={{ fontWeight: 700, color: mod.color, p: 0 }}>
                                        Acessar
                                    </Button>
                                </Box>
                            </FeatureCard>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
};

// ============================================================================
// PÁGINAS DE ERRO E REDIRECIONAMENTO
// ============================================================================
const AccessDeniedPage = () => {
    const navigateTo = process.env.REACT_APP_COCKPIT_URL;
    useEffect(() => { const timer = setTimeout(() => { window.location.href = navigateTo; }, 3000); return () => clearTimeout(timer); }, [navigateTo]);
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', textAlign: 'center', backgroundColor: '#050a30' }}>
            <Paper elevation={12} sx={{ p: 4, background: 'rgba(25, 35, 55, 0.7)', backdropFilter: 'blur(10px)', color: 'white', borderRadius: '15px' }}>
                <Typography variant="h4" sx={{ color: '#ff834f', mb: 2, fontWeight: 700 }}>Acesso Negado</Typography>
                <Typography>Você será redirecionado para a plataforma principal em 3 segundos...</Typography>
                <CircularProgress sx={{ mt: 3, color: '#ff834f' }} />
            </Paper>
        </Box>
    );
};

// ============================================================================
// GERENCIAMENTO DE ROTAS PROTEGIDAS
// ============================================================================
const AppRoutes = () => {
    const { user, loading } = useAuth();
    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>;
    if (!user) return <AccessDeniedPage />;

    return (
        <DashboardLayout>
            <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/visao-geral" element={<AdminOverviewPage />} />
                <Route path="/destaques" element={<TopPerformersPage />} />
                <Route path="/organograma" element={<OrganogramPageWrapper />} />
                <Route path="/competencias-corporativas" element={<CorporateCompetenciesPage />} />
                <Route path="/relatorios" element={<EmployeeAnalysisPage />} />
                <Route path="/graficos" element={<ChartsPage />} />
                <Route path="*" element={<Box sx={{ p: 5, textAlign: 'center' }}><Typography variant="h4">404 - Página Não Encontrada</Typography></Box>} />
            </Routes>
        </DashboardLayout>
    );
};

const App = () => (
    <BrowserRouter>
        <AuthProvider>
            <AppRoutes />
        </AuthProvider>
    </BrowserRouter>
);

export default App;